import { ReactElement, useMemo, useCallback } from 'react';
import { BankingDetailsCard, IDashboardQuarterSummary, ITargetValues, SummaryCard, TotalSiteCount } from '.';
import { useQuarterSummaryQuery } from '@services/hooks/dashboard/useQuarterSummaryQuery';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useMeetingFilterStore } from '@store/meetingFilterStore';
import { meetingInitialValues } from '@components/meetingModule/components/meeting-filter';
import { EndUserFilterStore } from '@store/memberList';
import { getQuarterStartEndDate } from '@helper/utils';
import { initialAllEndUsersFilter } from '@components/members/components/allEndUserList';
import { useSiteSummaryListingStore } from '@store/siteSummaryListingFilterStore';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useFactoryVisitListingStore } from '@store/factoryVisitListStore';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';
import { useEndUserNormsCount } from '@services/hooks/dashboard/useEndUserNormsCount';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { CMDIDropdownRBDMResponse } from '@services/hooks/endusers/cmdiDropdownRBDM';
import { useCMDIDropdownRBDMQuery } from '@services/hooks/endusers/useCMDIDropdownRBDMQuery';
import { useCMDIStore } from '@store/cmdiFilterStore';

export const QuarterSummary = (props: IDashboardQuarterSummary): ReactElement => {
	const { id } = props;
	const { selectedCMDI } = useCMDIStore();
	const { data, error, isFetching } = useQuarterSummaryQuery(selectedCMDI);
	const { updateFilter } = useMeetingFilterStore();
	const { updateFilter: siteLeadUpdate, allSiteSummaryFilter } = useSiteSummaryListingStore();
	const { updateFilter: factoryFilterUpdate, factoryVisitFilter } = useFactoryVisitListingStore();
	const { updateAllEndUserFilter } = EndUserFilterStore();
	const checkMeetingPermissions = useComponentLevelAccessCheck('meeting-banner');
	const checkMemberModulePermissions = useComponentLevelAccessCheck('member-module');
	const checkBDEFactoryVisitPermissions = useComponentLevelAccessCheck('bde-factory-visit');
	const checkTotalOutOffViewPermissions = useComponentLevelAccessCheck('total-out-off-view');
	const checkTotalOutOffViewSitePermissions = useComponentLevelAccessCheck('total-out-off-view-site');
	const checkKAESSLeadsPermissions = useComponentLevelAccessCheck('total-site-leads-from-kae');
	const checkCompetitionSitePermissions = useComponentLevelAccessCheck('total-competition-site-visited');
	const checkKaessSitePermissions = useComponentLevelAccessCheck('total-site-visited');
	const checkQuarterSummaryPermissions = useComponentLevelAccessCheck('quarter-summary');
	useCMDIDropdownRBDMQuery();
	const { data: quarterData } = useEndUserNormsCount();
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const { totalCount } = useGetRxDb('club');
	const { result: mdiCodeDropdownData } = useGetRxDb<CMDIDropdownRBDMResponse>('mdi_code_list');
	const userRole = useUserRoleType();

	const handleCardRedirect = (): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		updateFilter('allMeetingFilter', {
			...meetingInitialValues,
			meetingStatus: [
				{
					id: 1,
					value: 'Completed Meeting',
				},
			],
			meetingType: 'FCC PRODUCT AWARENESS MEET',
			filter_date: [start, end],
			...(selectedCMDI &&
				userRole === 'RBDM' && {
					mdicode: selectedCMDI,
				}),
		});
	};

	const handleAverageMeetAttendanceRedirection = (): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		updateFilter('allMeetingFilter', {
			...meetingInitialValues,
			meetingStatus: [
				{
					id: 1,
					value: 'Completed Meeting',
				},
			],
			filter_date: [start, end],
			...(selectedCMDI &&
				userRole === 'RBDM' && {
					mdicode: selectedCMDI,
				}),
		});
	};

	const handleSiteRedirection = (status?: string): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		siteLeadUpdate({
			...allSiteSummaryFilter,
			current_stage_of_site_c: status ?? '',
			lastactivitydate: [start, end],
			...(userRole === 'RBDM' && {
				mdicode: selectedCMDI,
			}),
		});
	};
	const handleAllSiteRedirection = (): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		if (checkKAESSLeadsPermissions) {
			siteLeadUpdate({
				...allSiteSummaryFilter,
				createddate: [start, end],
			});
			return;
		}
		siteLeadUpdate({
			...allSiteSummaryFilter,
			lastactivitydate: [start, end],
		});
	};
	const handleCompetitionSiteRedirection = (): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		siteLeadUpdate({
			...allSiteSummaryFilter,
			competition_site: true,
			lastactivitydate: [start, end],
		});
	};
	const handleFactoryRedirection = useCallback((): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		factoryFilterUpdate({
			...factoryVisitFilter,
			visitDate: [start, end],
		});
	}, [factoryFilterUpdate, factoryVisitFilter]);

	const handleAllEndUserFilter = (name: string): void => {
		const { start, end } = getQuarterStartEndDate(new Date());
		updateAllEndUserFilter({
			...initialAllEndUsersFilter,
			...(name === 'date' && {
				dateOfVisit: {
					...initialAllEndUsersFilter.dateOfVisit,
					startDate: start,
					endDate: end,
				},
			}),
			...(name === 'zbc' && {
				customToggle: { ...initialAllEndUsersFilter.customToggle, toggleData: ['ZBC'] },
			}),
			...(name === 'dgc' && {
				customToggle: { ...initialAllEndUsersFilter.customToggle, toggleData: ['Degrowing'] },
			}),
			// Include selected CMDI code in the filter if one is selected
			...(selectedCMDI && {
				mdicodeDropdown: {
					...initialAllEndUsersFilter.mdicodeDropdown,
					flagged: true,
					dropDownData: selectedCMDI,
				},
			}),
		});
	};

	const handleAdoptedContractorRedirection = (): void => {
		siteLeadUpdate({
			...allSiteSummaryFilter,
			competition_site: true,
			adopted_site: true,
			ssm_site: true,
			mdicode: profile[0]?.mdicode_c,
		});
	};

	const getEndUsersPath = (): string => {
		return userRole === 'RBDM' ? '/rbdm/end-users/all' : '/end-users/all';
	};

	const targetValues = useMemo<ITargetValues>(() => {
		const meetingCount = Math.ceil((totalCount ?? 0) / 2);
		return {
			meetingTarget: meetingCount,
			userTarget: quarterData?.target_for_quarter_count ?? 0,
			siteVisitTarget: TotalSiteCount[profile[0]?.usertype_c],
			mdicodeTarget: mdiCodeDropdownData.length,
		};
	}, [profile, quarterData, totalCount, mdiCodeDropdownData]);

	if (!checkQuarterSummaryPermissions) {
		return <></>;
	}

	return (
		<Wrapper isLoaderVisible={isFetching} isError={error}>
			<section key={id} className="quarter-summary-card">
				<div className="container">
					<span className="h3 cmn-heading">
						<span>Quarter Summary</span>
					</span>
					<div className="summary-card">
						{checkKAESSLeadsPermissions && (
							<SummaryCard
								heading="Sites from KAE"
								urlPath="/site-summary"
								value={data?.lead_received_in_quarter}
								summaryLeftIcon="icon-hammer"
								onCardClick={() => handleAllSiteRedirection()}
							/>
						)}
						{checkCompetitionSitePermissions && (
							<SummaryCard
								heading="Competition Site Visited"
								urlPath="/site-summary"
								value={data?.competition_site_visited}
								summaryLeftIcon="icon-coffee"
								onCardClick={handleCompetitionSiteRedirection}
							/>
						)}
						{checkKaessSitePermissions && (
							<SummaryCard
								urlPath="/site-summary"
								heading="All Visited Sites"
								value={data?.site_visits}
								summaryLeftIcon="icon-coffee"
								onCardClick={() => handleSiteRedirection('Site Visited')}
							/>
						)}
					</div>
					<div className="summary-card">
						{checkMeetingPermissions && (
							<SummaryCard
								heading="Total Meetings"
								urlPath={userRole === 'RBDM' ? '/rbdm/meetings/all-meetings' : '/meetings/all-meetings'}
								value={data?.total_meeting}
								summaryLeftIcon="icon-coffee"
								targetValue={targetValues.meetingTarget}
								onCardClick={handleCardRedirect}
								checkTotalOutOffViewPermissions={checkTotalOutOffViewPermissions}
							/>
						)}
						{checkMemberModulePermissions && (
							<>
								<SummaryCard
									heading="Total Site Visits"
									urlPath={userRole === 'RBDM' ? '/rbdm/site-summary' : '/site-summary'}
									value={data?.site_visits}
									summaryLeftIcon="icon-hammer"
									targetValue={targetValues.siteVisitTarget}
									onCardClick={() => handleSiteRedirection('Site Visited')}
									checkTotalOutOffViewPermissions={checkTotalOutOffViewSitePermissions}
								/>
								<SummaryCard
									heading="Total Users Added"
									urlPath={getEndUsersPath()}
									value={data?.total_user_enrollment}
									summaryLeftIcon="icon-user"
									targetValue={targetValues.userTarget}
									checkTotalOutOffViewPermissions={checkTotalOutOffViewPermissions}
									onCardClick={() => handleAllEndUserFilter('date')}
								/>
							</>
						)}
						{!checkBDEFactoryVisitPermissions && (
							<SummaryCard
								heading="Total Factory Visits"
								urlPath="/factory-visit"
								value={data?.factory_visits}
								summaryLeftIcon="icon-factory"
								onCardClick={handleFactoryRedirection}
							/>
						)}
					</div>
					{checkMemberModulePermissions && (
						<div className="zero-banking-user-card">
							<ul className="banking-details-list">
								<BankingDetailsCard
									title="0 Banking User"
									redirectTo={getEndUsersPath()}
									value={data?.zero_banking_users}
									onBankingCardClick={() => handleAllEndUserFilter('zbc')}
								/>
								<BankingDetailsCard
									title="De-growing Users"
									redirectTo={getEndUsersPath()}
									value={data?.de_growing_users}
									onBankingCardClick={() => handleAllEndUserFilter('dgc')}
								/>
							</ul>
						</div>
					)}

					{userRole === 'BDE' && (
						<div className="rbdm-tabs">
							<div className="zero-banking-user-card">
								<ul className="banking-details-list">
									<BankingDetailsCard
										title="SMM Competition Site Leads of Adopted Users"
										value={data?.adopted_contractor_sites_count}
										redirectTo="/site-summary"
										onBankingCardClick={() => handleAdoptedContractorRedirection()}
									/>
								</ul>
							</div>
						</div>
					)}

					{userRole === 'RBDM' && (
						<div className="rbdm-tabs">
							<div className="zero-banking-user-card">
								<ul className="banking-details-list">
									<BankingDetailsCard
										className={userRole === 'RBDM' ? 'rbdm-not-allowed-link' : ''}
										title="Total Verified Visits"
										value={data?.verified_visits}
										redirectTo={''}
										bankingRightIconVisible={userRole === 'RBDM'}
									/>
									<BankingDetailsCard
										className={userRole === 'RBDM' ? 'rbdm-not-allowed-link' : ''}
										title="Competition Sites Visited"
										value={data?.competition_site_visited}
										redirectTo={''}
										bankingRightIconVisible={userRole === 'RBDM'}
									/>
									<BankingDetailsCard
										title="Average Meet Attendance %"
										redirectTo={'/rbdm/meetings/all-meetings'}
										value={data?.average_meet_attendance}
										onBankingCardClick={() => handleAverageMeetAttendanceRedirection()}
									/>
								</ul>
							</div>
						</div>
					)}
				</div>
			</section>
		</Wrapper>
	);
};
